import { useEffect, useRef } from "react";
import { useLocation } from "@remix-run/react";

/**
 * Storefront pageview tracker.
 *
 * Discovered 2026-05-11: `/api/track-pageview` was wired on the server
 * (the analytics dashboards read `PageView` rows correctly) but NO
 * client code was calling it — the table only had historical rows from
 * some long-removed tracker. Result: retail-analytics showed 0 page
 * views even for days with real customer traffic. This hook closes the
 * loop.
 *
 * Session model: a UUID stored in sessionStorage (`sst_pv_session`).
 * Persists across SPA navigations within a tab; resets on tab close.
 * Matches the convention used by the retail-analytics
 * `COUNT(DISTINCT sessionId)` query that powers Unique Visitors.
 *
 * Dedup: server-side rate-limit at `api.track-pageview.ts` already
 * de-dupes same (session, path) within 5s, so we don't need to be
 * clever about client-side dedup. Just fire on every pathname change.
 *
 * Skipped paths:
 *   • /api/* — not a user-facing page
 *   • /admin/*, /portal/*, /rep/* — these aren't even routed through
 *     the _storefront layout, but defensive
 *   • SSR — only fires after hydration
 *
 * Transport: `navigator.sendBeacon` first (fire-and-forget, survives
 * navigation), `fetch` with `keepalive` as fallback.
 */
const SESSION_KEY = "sst_pv_session";

function getOrCreateSessionId(): string {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    // crypto.randomUUID is widely available; fall back to a timestamp
    // + Math.random for ancient browsers (Safari < 15.4).
    const id =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    // sessionStorage can throw in private-mode Safari etc. Fall back to
    // a per-render id (still better than nothing — won't dedupe across
    // navigations but at least the page view counts).
    return `nostore-${Date.now().toString(36)}`;
  }
}

function shouldTrack(pathname: string): boolean {
  if (!pathname) return false;
  if (pathname.startsWith("/api/")) return false;
  if (pathname.startsWith("/admin")) return false;
  if (pathname.startsWith("/portal")) return false;
  if (pathname.startsWith("/rep")) return false;
  return true;
}

export function usePageviewTracker() {
  const location = useLocation();
  // Use a ref to capture the referrer ONCE, on first mount. On SPA
  // navigations the referrer should be the previous page within the
  // SPA, not document.referrer (which is the EXTERNAL site that sent
  // the customer here). We send document.referrer on first track,
  // null on subsequent tracks.
  const isFirstTrack = useRef(true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const pathname = location.pathname + (location.search || "");
    if (!shouldTrack(location.pathname)) return;

    const sessionId = getOrCreateSessionId();
    const referrer = isFirstTrack.current
      ? document.referrer || null
      : null;
    isFirstTrack.current = false;

    const payload = JSON.stringify({
      path: pathname,
      sessionId,
      referrer,
    });

    try {
      // sendBeacon is fire-and-forget and survives page unloads.
      // Returns false if the user agent failed to enqueue (rare).
      if (typeof navigator.sendBeacon === "function") {
        const blob = new Blob([payload], { type: "application/json" });
        navigator.sendBeacon("/api/track-pageview", blob);
      } else {
        // Fallback: fetch with keepalive. `keepalive: true` lets the
        // request continue after the page unloads (e.g. during a fast
        // SPA route change).
        fetch("/api/track-pageview", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        }).catch(() => {
          /* analytics is best-effort; never surface failures to the user */
        });
      }
    } catch {
      /* analytics is best-effort; never surface failures to the user */
    }

    // Audit 2026-05-17 — Meta PageView (browser pixel + server CAPI mirror).
    // Why both: the Pixel SDK auto-PageView in root.tsx was REMOVED in
    // this same commit because that fire happens before AM is queued
    // and before the visitor_id cookie is set on a fresh session — it
    // shipped to Meta with minimal user_data (just ip/ua/fbp). Firing
    // from this useEffect runs AFTER hydration when the AM init has
    // already attached em/ph/fn/ln/etc. to the fbq queue. Mirror with
    // matching event_id so Meta dedups browser+server and counts ONE
    // PageView per pageview, enriched on both sides.
    //
    // Same dedup pattern as AddToCart in _storefront.tsx and
    // ViewContent in _storefront.products.$handle.tsx.
    try {
      const pvEventId =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `pv_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      if (typeof (window as any).fbq === "function") {
        (window as any).fbq("track", "PageView", {}, { eventID: pvEventId });
      }
      fetch("/api/fb-capi", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_name: "PageView",
          event_id: pvEventId,
          source_url: window.location.href,
        }),
        keepalive: true,
      }).catch(() => {
        /* CAPI is best-effort; never surface failures to the user */
      });
    } catch {
      /* never surface failures */
    }
  }, [location.pathname, location.search]);
}
